/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable max-len */
import { OnChangeFn, SortingState } from "@tanstack/react-table";
import { IconBook } from "@tabler/icons-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import uuid from "react-uuid";
import { Button } from "../../../../components/ui/button";
import { useAllPlaybooks } from "../../../../hooks/QueryHooks";
import { LegacyColumn } from "../../../../shared/table/legacyTanStackAdapter";
import TanStackTable from "../../../../shared/table/TanStackTable";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";

const PlaybooksPage = () => {
  const navigate = useNavigate();
  const { workspace }: any = useWorkspaceStore();

  const playbooks: any = useAllPlaybooks({
    workspaceId: workspace?.id,
    enabled: !!workspace?.id,
  });

  const legacyColumns = useMemo<LegacyColumn[]>(() => ([
    {
      key: "title",
      dataType: "string",
      title: "Playbook Title",
      colGroup: { style: { width: 360 } },
      isSortable: true,
    }, {
      key: "created_at",
      dataType: "string",
      title: "Created At",
      colGroup: { style: { width: 220 } },
      isSortable: true,
    }, {
      key: "updated_at",
      dataType: "string",
      title: "Last Updated",
      colGroup: { style: { width: 220 } },
      isSortable: true,
    }, {
      key: "playbook_actions",
      dataType: "playbook_actions",
      title: "",
      colGroup: { style: { width: 110 } },
      isSortable: false,
    },
  ]), []);

  const allRows = useMemo(() => (
    (playbooks?.data || []).map((item: any) => ({
      ...item,
      randomId: item?.id || uuid(),
    }))
  ), [playbooks?.data]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const [pageIndex, setPageIndex] = useState<number>(0);
  const pageSize = 15;

  const sortedRows = useMemo(() => {
    if (!sorting?.length) return allRows;

    const activeSort = sorting[0];
    if (!activeSort?.id) return allRows;

    return [...allRows].sort((a, b) => {
      const aValue = a?.[activeSort.id];
      const bValue = b?.[activeSort.id];

      if (aValue === bValue) return 0;
      if (aValue === undefined || aValue === null) return activeSort.desc ? 1 : -1;
      if (bValue === undefined || bValue === null) return activeSort.desc ? -1 : 1;

      const aDate = Date.parse(String(aValue));
      const bDate = Date.parse(String(bValue));
      const bothDates = !Number.isNaN(aDate) && !Number.isNaN(bDate);

      const diff = bothDates
        ? aDate - bDate
        : String(aValue).localeCompare(String(bValue), undefined, { numeric: true, sensitivity: "base" });

      return activeSort.desc ? -diff : diff;
    });
  }, [allRows, sorting]);

  const totalRows = sortedRows.length;
  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(totalRows / pageSize)),
    [totalRows],
  );

  useEffect(() => {
    if (pageIndex < pageCount) return;
    setPageIndex(Math.max(0, pageCount - 1));
  }, [pageIndex, pageCount]);

  const pageRows = useMemo(() => {
    const start = pageIndex * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, pageIndex, pageSize]);

  const onTableSortingChange: OnChangeFn<SortingState> = useCallback((updater) => {
    const nextSorting = typeof updater === "function" ? updater(sorting) : updater;
    setSorting((nextSorting || []).slice(0, 1));
  }, [sorting]);

  return (
    <div className="relative h-full overflow-auto bg-background px-6 py-6">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-4">
        <div className="flex items-center justify-between">
          <h2 className="text-3xl font-bold tracking-tight text-foreground">Your Playbooks</h2>
          <Button
            size="sm"
            onClick={() => navigate("new")}
            className="gap-2"
          >
            <IconBook className="h-4 w-4" />
            New Playbook
          </Button>
        </div>

        <TanStackTable
          legacyColumns={legacyColumns}
          data={pageRows}
          totalData={totalRows}
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageCount={pageCount}
          onPageIndexChange={setPageIndex}
          sorting={sorting}
          onSortingChange={onTableSortingChange}
          loading={playbooks?.isLoading}
          itemType="Playbooks"
          noDataTitle="No Playbooks Found"
          noDataMessage="Create your first playbook to get started."
          height={window.innerHeight - 260}
          surfaceVariant="lovable"
          enableColumnPinning={false}
        />
      </div>
    </div>
  );
};

export default PlaybooksPage;
